<template>
  <div>
    <!-- ===================== MOBILE CARD LIST (< 960px) ===================== -->
    <div class="d-md-none sales-mobile-list mt-2 px-2 pb-4">
      <v-progress-linear
        v-if="loading"
        indeterminate
        color="#a6160b"
        class="mb-2"
      />

      <div
        v-for="{
          item,
          index,
          mobilePrice,
          qrId,
          imageSrc,
          title,
          statusLabel,
          statusClickable,
        } in mobileSaleCards"
        :key="item._id"
        class="sales-mobile-card"
        :class="{
          'sales-mobile-card--selected': index === activeRow,
          'sales-mobile-card--active': item.return_date >= currentDate,
          'sales-mobile-card--finished': item.return_date < currentDate,
        }"
        @click="activeRow = activeRow === index ? null : index"
      >
        <v-btn
          v-if="mobilePrice"
          class="sales-mobile-card__price"
          variant="outlined"
          size="x-small"
          @click.stop
        >
          <del
            v-if="mobilePrice.isDiscounted"
            class="text-black overline mr-1"
          >
            {{ mobilePrice.original }}
          </del>
          <span
            :class="
              mobilePrice.isDiscounted
                ? 'text-red font-weight-bold'
                : 'text-black'
            "
          >
            {{ mobilePrice.text }}
          </span>
        </v-btn>

        <div class="sales-mobile-card__main">
          <div class="sales-mobile-card__media">
            <button
              type="button"
              class="sales-mobile-card__image-btn"
              @click.stop="onSaleImageClick(item)"
            >
              <img :src="imageSrc" class="sales-mobile-card__img" />
            </button>
            <button
              v-if="qrId"
              type="button"
              class="sales-mobile-card__qr-btn"
              @click.stop="openSaleQr(item)"
            >
              {{ qrId }}
            </button>
          </div>

          <div class="sales-mobile-card__info">
            <div class="sales-mobile-card__info-top">
              <v-chip
                v-if="
                  ($store.getters.role === 'LekoAdmin' ||
                    $store.getters.role === 'SuperAdmin') &&
                  item.institution_id?.title
                "
                   class=" text-xs"
                 size="x-small"
              >
                {{ item.institution_id.title }}
              </v-chip>
              <div
                v-if="title"
                class="font-weight-bold text-primary text-body-2"
              >
                {{ title }}
              </div>
              <div v-else-if="item.type === 'Reservation'" class="text-body-2">
                {{ item.reservation_part?.description }}
              </div>
            </div>

            <div v-if="item.part_id?.manufacturer_code" class="text-caption mt-1">
              Cod: <strong>{{ item.part_id.manufacturer_code }}</strong>
            </div>
            <div v-if="item.part_id?.pra" class="text-caption mt-1">
              PRA: <strong>{{ item.part_id.pra }}</strong>
            </div>
            <div class="text-caption">
              <span
                v-if="item.part_id?.shipment_id?.name"
                :style="item.part_id?.car_id ? 'color:#1174d0' : ''"
              >
                {{ item.part_id.shipment_id.name }}
              </span>
              <span v-else>-</span>
            </div>
            <div
              v-if="
                (item.client?.name && item.client?.surname) ||
                item.customShopUser === 'lekoeBay' ||
                item.customShopPlatformSold === 'ReteRicambi'
              "
              class="text-caption mt-1 d-flex align-center ga-1 flex-wrap"
            >
              <span>
                {{ $t("sales.table.client") }}:
              </span>
              <span v-if="item.client?.name && item.client?.surname">
              <strong>
                {{ item.client.name }} {{ item.client.surname }}
                </strong>
              </span>
              <span v-else-if="item.customShopUser === 'lekoeBay'">eBay</span>
              <template
                v-else-if="item.customShopPlatformSold === 'ReteRicambi'"
              >
                <span>{{ $t("sales.table.sold_retericambi") }}</span>
                <img
                  height="20"
                  width="20"
                  src="@/assets/rr-logo.png"
                  alt="ReteRicambi"
                />
              </template>
            </div>
            <div v-if="item.debt" class="text-caption mt-1">
              {{ difference(item.debt) }}
            </div>
            <div
              v-if="item.date_created"
              class="text-caption"
            >
              {{ $t("sales.table.date_created") }}:
              {{ formatDate(item.date_created) }}
            </div>
            <div
              v-if="item.return_date"
              class="text-caption"
            >
              {{ $t("sales.table.return_date") }}:
              {{ formatDate(item.return_date) }}
            </div>
            <div
              v-if="item.part_id?.quality || item.warranty"
              class="d-flex align-center flex-wrap ga-3 mt-1"
            >
              <div
                v-if="item.part_id?.quality"
                class="d-flex align-center ga-1"
              >
                <v-rating
                  :model-value="item.part_id.quality.rating"
                  color="#a6160b"
                  readonly
                  :size="14"
                  density="compact"
                />
              </div>
              <div v-if="item.warranty" class="d-flex align-center ga-1 mt-2">
                <span class="text-caption">
                  {{ $t("sales.table.warranty") }}:
                </span>
                <span class="text-caption">
                  {{ item.warranty.return_days / 86400 }}
                  {{ $t("warranties.days") }}
                </span>
              </div>
            </div>
            <div v-if="item.isNewPart" class="mt-1">
              <v-icon size="16" color="#a6160b">mdi-check-circle-outline</v-icon>
            </div>
            <div
              v-if="item.notes"
              class="text-caption mt-1 text-truncate"
              @click.stop="showNotes(item)"
            >
              {{ item.notes }}
            </div>
          </div>
        </div>

        <div class="sales-mobile-card__actions">
          <v-chip
            v-if="statusLabel"
            size="small"
            color="#a6160b"
            variant="tonal"
            class="mr-1 text-caption font-weight-medium"
            :class="{ pointer: statusClickable }"
            @click.stop="statusClickable && movement(item.part_id)"
          >
            {{ statusLabel }}
          </v-chip>

          <v-spacer />

          <v-btn
            v-if="
              !item?.part_id?.car_id &&
              (!item?.part_id?.infoCar?.metadata ||
                item.part_id.infoCar.metadata.length === 0)
            "
            icon
            variant="text"
            size="small"
            disabled
          >
            <v-icon size="20" color="grey-darken-2">mdi-playlist-remove</v-icon>
          </v-btn>
          <v-btn
            v-else
            icon
            variant="text"
            size="small"
            :disabled="!item.part_id || item.type === 'Reservation'"
            @click.stop="details(item.part_id)"
          >
            <v-icon size="20" color="grey-darken-2">mdi-view-list</v-icon>
          </v-btn>
          <v-btn
            icon
            variant="text"
            size="small"
            :disabled="!item.part_id || item.type === 'Reservation'"
            @click.stop="print(item)"
          >
            <v-icon size="20" color="grey-darken-2">mdi-printer</v-icon>
          </v-btn>
          <v-btn icon variant="text" size="small" @click.stop="share(item)">
            <v-icon size="20" color="grey-darken-2">
              mdi-share-variant-outline
            </v-icon>
          </v-btn>
          <v-btn
            v-if="
              (item.status === 'Sold' || item.status === 'SoldCustomShop') &&
              item.part_id &&
              item.part_id.status != 2 &&
              item.part_id.status !== 2.1
            "
            icon
            variant="text"
            size="small"
            @click.stop="returnE(item)"
          >
            <v-icon size="20" color="grey-darken-2">
              mdi-compare-horizontal
            </v-icon>
          </v-btn>
          <v-btn
            v-if="!checkCar(item)"
            icon
            variant="text"
            size="small"
            @click.stop="viewCar(item.part_id.car_id)"
          >
            <v-icon size="20" color="grey-darken-2">mdi-car</v-icon>
          </v-btn>
          <v-btn v-else icon variant="text" size="small" disabled>
            <v-icon size="20" color="grey-darken-2">mdi-car-off</v-icon>
          </v-btn>
          <v-btn
            icon
            variant="text"
            size="small"
            @click.stop="viewPart(item.part_id)"
          >
            <v-icon size="20" color="grey-darken-2">
              mdi-dots-horizontal-circle-outline
            </v-icon>
          </v-btn>

          <v-dialog v-model="item.editNotesDialog" max-width="400">
            <template #activator="{ props }">
              <v-btn icon variant="text" size="small" v-bind="props" @click.stop>
                <v-icon size="20" color="grey-darken-2">mdi-pencil</v-icon>
              </v-btn>
            </template>
            <v-card>
              <v-card-text>
                <v-text-field
                  v-model="item.notes"
                  label="Notes"
                  color="red-darken-1"
                  autofocus
                />
              </v-card-text>
              <v-card-actions class="justify-end">
                <v-btn
                  color="grey-darken-4"
                  variant="text"
                  @click="item.editNotesDialog = false"
                >
                  {{ $t("parts.inventory.form.close") }}
                </v-btn>
                <v-btn
                  color="green"
                  variant="outlined"
                  @click="saveNotes(item._id, item.notes)"
                >
                  {{ $t("parts.inventory.form.save") }}
                </v-btn>
              </v-card-actions>
            </v-card>
          </v-dialog>
        </div>
      </div>

      <v-alert
        v-if="!loading && sales.length < 1"
        type="info"
        variant="tonal"
        class="mt-2"
      >
        No data available.
      </v-alert>
    </div>
    <!-- ===================================================================== -->

    <div class="sales-table-host pb-2">
      <StickyHScroll>
        <v-data-table-server
        v-model:options="pagination"
        :headers="headers"
        :items="sales"
        :page="pagination.page"
        :items-length="totalItems"
        :loading="loading"
        class="elevation-1 nowrap-headers pb-8"
        :items-per-page="pagination.itemsPerPage"
        :items-per-page-options="[
          { value: 5, title: '5' },
          { value: 10, title: '10' },
          { value: 25, title: '25' },
          { value: 50, title: '50' },
        ]"
        items-per-page-text="Rows per page"
        @update:options="updatePagination"
      >
        <template #progress>
          <v-progress-linear color="#a6160b" indeterminate />
        </template>
        <template #item="{ item, index }">
          <tr
            :class="{
              activeSaleRow: item.return_date >= currentDate,
              finishedSaleRow: item.return_date < currentDate,
              tableRowColorSelected: index === activeRow,
            }"
            @click="activeRow = activeRow === index ? null : index"
          >
            <!------------------------------------------ID------------------------------------------->
            <!--OLD ID-->
            <td
              v-if="
                item.part && item.part.old_id && item.part.old_id !== undefined
              "
            >
              <v-row
                class="pointer ma-1 text-center justify-center d-block"
                @click="
                  openQrDialog({
                    id: item.part.old_id,
                    type: 'inventory',
                    printed: item.part.printed,
                  })
                "
              >
                <qr-code
                  class="d-flex justify-center"
                  :size="60"
                  :text="item.part.old_id"
                />
                {{
                  item.part.old_id.slice(
                    item.part.old_id.length - 6,
                    item.part.old_id.length,
                  )
                }}
              </v-row>
            </td>
            <!--NEW ID-->
            <td v-else class="text-xs">
              <v-row
                class="pointer ma-1 text-center justify-center d-block"
                @click="
                  openQrDialog({
                    id: item.part._id,
                    type: 'inventory',
                    printed: item.part.printed,
                  })
                "
              >
                <qr-code
                  class="d-flex justify-center"
                  :size="60"
                  :text="item.part._id"
                />
                {{
                  item.part._id.slice(
                    item.part._id.length - 6,
                    item.part._id.length,
                  )
                }}
              </v-row>
            </td>
            <!----------------------------------------ACTIONS---------------------------------------->
            <td>
              <v-row no-gutters class="d-flex flex-nowrap">
                <v-btn
                  v-if="
                    !item?.part_id?.car_id &&
                    (!item?.part_id?.infoCar?.metadata ||
                      item.part_id.infoCar.metadata.length === 0)
                  "
                  icon
                  variant="text"
                  class="ma-0"
                  disabled
                >
                  <v-icon size="large" color="grey-darken-2"
                    >mdi-playlist-remove</v-icon
                  >
                </v-btn>
                <v-btn
                  v-else
                  icon
                  variant="text"
                  class="ma-0"
                  :disabled="!item.part_id || item.type === 'Reservation'"
                  @click="details(item.part_id)"
                >
                  <v-icon color="grey-darken-2">mdi-view-list</v-icon>
                </v-btn>
                <v-btn
                  :disabled="!item.part_id || item.type === 'Reservation'"
                  icon
                  variant="text"
                  @click="print(item)"
                >
                  <v-icon color="grey-darken-2"> mdi-printer </v-icon>
                </v-btn>
                <v-btn icon variant="text" class="ma-0" @click="share(item)">
                  <v-icon color="grey-darken-2"
                    >mdi-share-variant-outline</v-icon
                  >
                </v-btn>
              </v-row>
              <v-row no-gutters class="d-flex flex-nowrap">
                <!-- :disabled="
                  (item.part_id &&
                    item.part_id.order &&
                    item.part_id.order.value === true) ||
                  item.type === 'Reservation'
                " -->
                <v-btn
                  v-if="
                    (item.status === 'Sold' ||
                      item.status === 'SoldCustomShop') &&
                    item.part_id.status != 2 &&
                    item.part_id.status !== 2.1
                  "
                  icon
                  variant="text"
                  @click="returnE(item)"
                >
                  <v-icon color="grey-darken-2">
                    mdi-compare-horizontal
                  </v-icon>
                </v-btn>
                <v-btn
                  v-if="!checkCar(item)"
                  icon
                  variant="text"
                  @click="viewCar(item.part_id.car_id)"
                >
                  <v-icon color="grey-darken-2"> mdi-car </v-icon>
                </v-btn>
                <v-btn v-else icon variant="text" disabled>
                  <v-icon color="grey-darken-2"> mdi-car-off </v-icon>
                </v-btn>
                <v-btn icon variant="text" @click="viewPart(item.part_id)">
                  <v-icon color="grey-darken-2">
                    mdi-dots-horizontal-circle-outline
                  </v-icon>
                </v-btn>
              </v-row>
            </td>
            <!---------------------------------------SHIPMENT---------------------------------------->
            <td class="pa-2" style="min-width: 140px">
              <span
                v-if="
                  item.part_id &&
                  item.part_id.shipment_id &&
                  item.part_id.shipment_id.name
                "
              >
                {{ item.part_id.shipment_id.name }}<br />
              </span>
              <span v-if="item.part_id && item.part_id.shelf_data">
                {{ item.part_id.shelf_data.shelf.room_name }} -
                {{ item.part_id.shelf_data.shelf.location_code }}:
                {{ item.part_id.shelf_data.shelf.x }},
                {{ item.part_id.shelf_data.shelf.y }}<br />
                <span class="caption">Mag: </span>
                <span class="font-weight-bold caption">{{
                  item.part_id.shelf_data.repo
                }}</span>
              </span>
              <span v-else> - </span>
            </td>
            <!-----------------------------------------TITLE----------------------------------------->
            <td class="text-xs">
              <v-row v-if="item.type === 'Reservation'">
                <span class="text-xs font-weight-bold">
                  {{ item.reservation_part.description }}
                </span>
              </v-row>
              <v-row v-else column>
                <v-col
                  v-if="
                    item.part_id &&
                    item.part_id.infoCar &&
                    item.part_id.infoCar.description
                  "
                >
                  <span
                    v-if="
                      item.part_id.infoCar.description.descriptionId &&
                      item.part_id.infoCar.description.descriptionId.title
                    "
                    class="text-xs font-weight-bold"
                  >
                    <!-- eslint-disable-next-line max-len -->
                    {{
                      item.part_id.infoCar.description.descriptionId.title[
                        $store.getters.language
                      ]
                    }}
                  </span> 
                  <span
                    v-if="item.part_id.infoCar.description.positionCode"
                    class="text-xs"
                  >&nbsp
                    {{
                      getPosition(item.part_id.infoCar.description.positionCode)
                    }}
                  </span>
                </v-col>
              </v-row>
            </td>
            <!-----------------------------------MANUFACTURER CODE----------------------------------->
            <td>
              <span v-if="item.part_id">
                {{ item.part_id.manufacturer_code }}
              </span>
              <span v-else />
            </td>
            <!--------------------------------------PRA NUMBER--------------------------------------->
            <td>
              <span v-if="item.part_id">
                {{ item.part_id.pra }}
              </span>
              <span v-else />
            </td>
            <!--------------------------------------INSTITUTION-------------------------------------->
            <td
              v-if="
                $store.getters.role === 'LekoAdmin' ||
                $store.getters.role === 'SuperAdmin'
              "
            >
              <span
                v-if="
                  ($store.getters.role === 'LekoAdmin' ||
                    $store.getters.role === 'SuperAdmin') &&
                  item.institution_id &&
                  item.institution_id.title
                "
              >
                <v-chip>
                  {{ item.institution_id.title }}
                </v-chip>
              </span>
              <span
                v-else-if="
                  ($store.getters.role === 'LekoAdmin' ||
                    $store.getters.role === 'SuperAdmin') &&
                  (!item.institution_id || !item.institution_id.title)
                "
              />
            </td>
            <!----------------------------------------IMAGES----------------------------------------->
            <td>
              <v-row align-center justify-center>
                <img
                  v-if="
                    item.type === 'Reservation' &&
                    item.reservation_part_id &&
                    item.reservation_part_id._id &&
                    item.reservation_part_id.images
                  "
                  :src="
                    item.reservation_part_id &&
                    item.reservation_part_id._id &&
                    item.reservation_part_id.images
                      ? `https://leko-tech.fra1.cdn.digitaloceanspaces.com/${item.reservation_part_id.images[0]}`
                      : `https://portal.lekotech.com/logo_small.png`
                  "
                  class="ma-1 pointer"
                  height="75"
                  width="100"
                  @click="
                    showResImages(
                      item.reservation_part_id._id,
                      item.reservation_part_id.images,
                    )
                  "
                />
                <img
                  v-else-if="
                    item.part_id && item.part_id._id && item.part_id.images
                  "
                  :src="
                    item.part_id &&
                    item.part_id._id &&
                    item.part_id.images &&
                    item.part_id.images.length > 0 &&
                    item.part_id.images[0].thumb
                      ? `https://leko-tech.fra1.cdn.digitaloceanspaces.com/${item.part_id.images[0].thumb}`
                      : `https://portal.lekotech.com/logo_small.png`
                  "
                  class="ma-1 pointer"
                  height="75"
                  width="100"
                  alt="photo"
                  @click="
                    showImages(
                      item.part_id._id,
                      item.part_id.images,
                      item.part_id.date_created,
                    )
                  "
                />
              </v-row>
            </td>
            <!-------------------------------------CONDITION-------------------------------------->
            <td class="text-xs">
              <span v-if="item.isNewPart">
                <v-icon>mdi-check-circle-outline</v-icon>
              </span>
            </td>
            <!--------------------------------------PART NOTES--------------------------------------->
            <td>
              <v-row class="pointer justify-center" @click="showNotes(item)">
                <v-col v-if="item.type === 'Reservation'">
                  <span
                    v-if="
                      item.reservation_part &&
                      item.reservation_part.notes &&
                      item.reservation_part.notes.length > 40
                    "
                  >
                    {{ item.reservation_part.notes.substring(0, 40) }}...
                    <v-icon
                      v-if="
                        item.reservation_part &&
                        item.reservation_part.notes &&
                        item.reservation_part.notes.length > 40
                      "
                      >mdi-eye</v-icon
                    >
                  </span>
                  <span v-else-if="item.reservation_part">
                    {{ item.reservation_part.notes }}
                  </span>
                </v-col>
                <v-col v-else-if="item.part_id">
                  <span
                    v-if="item.part_id.notes && item.part_id.notes.length > 40"
                  >
                    {{ item.part_id.notes.substring(0, 40) }}...<v-icon
                      v-if="
                        item.part_id &&
                        item.part_id.notes &&
                        item.part_id.notes.length > 40
                      "
                      >mdi-eye</v-icon
                    >
                  </span>
                  <span v-else> {{ item.part_id.notes }} </span>
                </v-col>
              </v-row>
            </td>
            <!--------------------------------------SALE NOTES--------------------------------------->
            <td>
              <v-row class="text-xs text-center justify-center">
                <!-- Display existing note -->
                <div v-if="item && item.notes">{{ item.notes }}</div>
                <div v-else />

                <!-- Edit Dialog -->
                <v-dialog v-model="item.editNotesDialog" max-width="400">
                  <template #activator="{ props }">
                    <v-btn icon variant="text" v-bind="props">
                      <v-icon color="grey-darken-2">mdi-pencil</v-icon>
                    </v-btn>
                  </template>

                  <v-card>
                    <v-card-text>
                      <v-text-field
                        v-model="item.notes"
                        label="Notes"
                        color="red-darken-1"
                        autofocus
                      />
                    </v-card-text>
                    <v-card-actions class="justify-end">
                      <v-btn
                        color="grey-darken-4"
                        variant="text"
                        @click="item.editNotesDialog = false"
                      >
                        {{ $t("parts.inventory.form.close") }}</v-btn
                      >
                      <v-btn
                        color="green"
                        variant="outlined"
                        @click="saveNotes(item._id, item.notes)"
                      >
                        {{ $t("parts.inventory.form.save") }}</v-btn
                      >
                    </v-card-actions>
                  </v-card>
                </v-dialog>
              </v-row>
            </td>
            <!----------------------------------------CLIENT----------------------------------------->
            <td v-if="item.client && item.client.name && item.client.surname">
              {{ item.client.name }} {{ item.client.surname }}
            </td>
            <td
              v-else-if="
                item.customShopUser && item.customShopUser == 'lekoeBay'
              "
            >
              eBay
            </td>
            <td v-else></td>
            <!-----------------------------------------DEBT------------------------------------------>
            <td>
              <span v-if="item.debt">{{ difference(item.debt) }}</span>
              <span v-else />
            </td>
            <!-----------------------------------------PRICE----------------------------------------->
            <td class="text-center">
              <!-- Reservation price (unchanged) -->
              <span
                v-if="
                  item.type === 'Reservation' &&
                  item.reservation_part.price &&
                  item.reservation_part.currency
                "
              >
                {{ item.reservation_part.price }}
                {{ item.reservation_part.currency }}
              </span>

              <!-- Normal part with discount -->
              <template
                v-else-if="item.part && item.part.price && item.part.currency"
              >
                <!-- create local variable: discount -->
                <template
                  v-for="discount in [
                    calculateDiscountedPrice(item.part.price, item),
                  ]"
                >
                  <v-btn variant="outlined">
                    <del
                      v-if="discount.isDiscounted"
                      class="text-black overline mr-2"
                    >
                      {{ item.part.price }} {{ item.part.currency }}
                    </del>

                    <span
                      :class="
                        discount.isDiscounted
                          ? 'text-red font-weight-bold'
                          : 'text-black'
                      "
                    >
                      {{ discount.finalPrice }} {{ item.part.currency }}
                    </span>
                  </v-btn>

                  <!-- <div
                  v-if="discount.isDiscounted"
                  class="caption mt-1 text-red text-center font-weight-bold"
                >
                  ( -{{ discount.discount }}% OFF )
                </div> -->
                  <!-- <div
                  v-if="discount.isDiscounted"
                  class="caption mt-1 text-red text-center font-weight-bold"
                >
                  ( {{ $t("sales.table.price") }} - %RR + 7% +
                  {{ $t("parts.inventory.form.shipping_costs") }} )
                </div> -->
                </template>
              </template>

              <span v-else />
            </td>

            <!---------------------------------------LOCATION---------------------------------------->
            <!-- <td
            v-if="item.part_id && item.part_id.shelf_data"
            class="text-xs"
          >
            <span>
              {{ item.part_id.shelf_data.shelf.room_name }} -
              {{ item.part_id.shelf_data.shelf.location_code }}:&nbsp;
              {{ item.part_id.shelf_data.shelf.x }},
              {{ item.part_id.shelf_data.shelf.y }}
            </span>

            <div class="text-no-wrap ">
              <span class="caption">Mag: </span>
              <span class="font-weight-bold caption">{{ item.part_id.shelf_data.repo }}</span>
            </div>
          </td>
          <td
            v-else
            class="text-xs"
          >
            -
          </td> -->
            <!----------------------------------------QUALITY---------------------------------------->
            <td v-if="item.part_id && item.part_id.quality" class="pr-2">
              <div style="width: 75px">
                <v-rating
                  :model-value="item.part_id.quality.rating"
                  color="#a6160b"
                  background-color="red-darken-4"
                  readonly
                  dense
                  :size="14"
                />
              </div>
            </td>
            <td v-else />
            <!----------------------------------------WARRANTY--------------------------------------->
            <td v-if="item.warranty">
              {{ item.warranty.return_days / 86400 }}
              {{ $t("warranties.days") }}
            </td>
            <td v-else />
            <!-------------------------------------DATE CREATED-------------------------------------->
            <td>
              <v-row
                v-if="item.date_created"
                class="pl-2 text-center text-truncate"
              >
                {{ formatDate(item.date_created) }}
              </v-row>
              <span v-else />
            </td>
            <!--------------------------------------RETURN DATE-------------------------------------->
            <td>
              <v-row v-if="item.return_date" class="text-center text-truncate">
                {{ formatDate(item.return_date) }}
              </v-row>
              <span v-else />
            </td>
            <!---------------------------STATUS (TAKEN FROM THE PART DOC)---------------------------->
            <td>
              <v-row class="text-end justify-end">
                <!--SOLD BUT THE PART IS MISSING-->
                <v-btn
                  v-if="item.status === 'Sold' && !item.part_id"
                  small
                  color="#a6160b"
                  variant="outlined"
                >
                  {{ $t("sales.table.sold_desk") }}
                </v-btn>
                <v-btn
                  v-else-if="item.status === 'SoldCustomShop' && !item.part_id"
                  small
                  color="#a6160b"
                  variant="outlined"
                >
                  {{ $t("sales.table.sold_ebay") }}
                </v-btn>
                <!--SOLD-->
                <v-btn
                  v-if="
                    item.part_id &&
                    item.part_id.status === 5 &&
                    item.status &&
                    item.status === 'SoldCustomShop' &&
                    item.customShopUser &&
                    item.customShopUser === 'multibreves'
                  "
                  small
                  color="#a6160b"
                  variant="outlined"
                  @click="movement(item.part_id)"
                >
                  <span
                    v-if="
                      item.customShopPlatformSold &&
                      item.customShopPlatformSold === 'Ovoko'
                    "
                  >
                    {{ $t("sales.table.sold_ovoko") }}
                  </span>
                  <span
                    v-else-if="
                      item.customShopPlatformSold &&
                      item.customShopPlatformSold === 'eBay'
                    "
                  >
                    {{ $t("sales.table.sold_ebay") }}
                  </span>
                  <!--Set the default platform as eBay (That's how it was before we specified it)-->
                  <span v-else>
                    {{ $t("sales.table.sold_ebay") }}
                  </span>
                </v-btn>
                <v-btn
                  v-else-if="
                    item.part_id &&
                    item.part_id.status === 5 &&
                    item.status &&
                    item.status === 'SoldCustomShop' &&
                    item.customShopUser &&
                    item.customShopUser === 'lekoeBay'
                  "
                  small
                  color="#a6160b"
                  variant="outlined"
                  @click="movement(item.part_id)"
                >
                  {{ $t("sales.table.sold_ebay_leko") }}
                </v-btn>
                <v-btn
                  v-else-if="
                    item.part_id &&
                    item.part_id.status === 5 &&
                    item.status &&
                    item.status === 'SoldCustomShop'
                  "
                  small
                  color="#a6160b"
                  variant="outlined"
                  @click="movement(item.part_id)"
                >
                  {{ $t("sales.table.sold_online") }}
                </v-btn>
                <!--sold on retericambi-->
                <v-btn
                  v-else-if="
                    item.part_id &&
                    item.part_id.status === 5 &&
                    item.status &&
                    item.status === 'Sold' &&
                    item.customShopPlatformSold &&
                    item.customShopPlatformSold === 'ReteRicambi'
                  "
                  small
                  color="#a6160b"
                  variant="outlined"
                  @click="movement(item.part_id)"
                >
                  {{ $t("sales.table.sold_retericambi") }}
                  <img
                    height="20"
                    width="20"
                    class="ml-auto"
                    src="@/assets/rr-logo.png"
                  />
                </v-btn>
                <v-btn
                  v-else-if="
                    item.part_id &&
                    item.part_id.status === 5 &&
                    item.status &&
                    item.status === 'Sold'
                  "
                  small
                  color="#a6160b"
                  variant="outlined"
                  @click="movement(item.part_id)"
                >
                  {{ $t("sales.table.sold_desk") }}
                </v-btn>

                <!--ENTERED (HIGHLY UNLIKELY TO EVER HAVE THIS STATUS)-->
                <v-btn
                  v-if="item.part_id && item.part_id.status === 1"
                  small
                  color="#a6160b"
                  variant="outlined"
                  @click="movement(item.part_id)"
                >
                  {{ $t("parts.status.entered") }}
                </v-btn>

                <!--RETURNED-SALES-->
                <v-btn
                  v-if="item.part_id && item.part_id.status === 2"
                  small
                  color="#a6160b"
                  variant="outlined"
                  @click="movement(item.part_id)"
                >
                  {{ $t("parts.status.returned") }}
                </v-btn>

                <!--RETURNED-SALES-->
                <v-btn
                  v-if="item.part_id && item.part_id.status === 2.1"
                  small
                  color="#a6160b"
                  variant="outlined"
                  @click="movement(item.part_id)"
                >
                  {{ $t("parts.status.returned_shop") }}
                </v-btn>

                <!--RE-ENTERED-->
                <v-btn
                  v-if="item.part_id && item.part_id.status === 3"
                  small
                  color="#a6160b"
                  variant="outlined"
                  @click="movement(item.part_id)"
                >
                  {{ $t("parts.status.re_entered") }}
                </v-btn>

                <!--RE-ENTERED-->
                <v-btn
                  v-if="item.part_id && item.part_id.status === 4"
                  small
                  color="#a6160b"
                  variant="outlined"
                  @click="movement(item.part_id)"
                >
                  {{ $t("parts.status.found") }}
                </v-btn>

                <!--LOST-->
                <v-btn
                  v-if="item.part_id && item.part_id.status === 6"
                  small
                  color="#a6160b"
                  variant="outlined"
                  @click="movement(item.part_id)"
                >
                  {{ $t("parts.status.lost") }}
                </v-btn>

                <!--LOST-->
                <v-btn
                  v-if="item.part_id && item.part_id.status === 6.1"
                  small
                  color="#a6160b"
                  variant="outlined"
                  @click="movement(item.part_id)"
                >
                  {{ $t("parts.status.lost_shop") }}
                </v-btn>

                <!--DAMAGED-->
                <v-btn
                  v-if="item.part_id && item.part_id.status === 7"
                  small
                  color="#a6160b"
                  variant="outlined"
                  @click="movement(item.part_id)"
                >
                  {{ $t("parts.status.damaged") }}
                </v-btn>

                <!--DAMAGED SALES-->
                <v-btn
                  v-if="item.part_id && item.part_id.status === 8"
                  small
                  color="#a6160b"
                  variant="outlined"
                  @click="movement(item.part_id)"
                >
                  {{ $t("parts.status.damaged_sales") }}
                </v-btn>

                <!--DAMAGED SHOP-->
                <v-btn
                  v-if="item.part_id && item.part_id.status === 8.1"
                  small
                  color="#a6160b"
                  variant="outlined"
                  @click="movement(item.part_id)"
                >
                  {{ $t("parts.status.damaged_shop") }}
                </v-btn>

                <!--DAMAGED LOST-->
                <v-btn
                  v-if="item.part_id && item.part_id.status === 9"
                  small
                  color="#a6160b"
                  variant="outlined"
                  @click="movement(item.part_id)"
                >
                  {{ $t("parts.status.damaged_shop") }}
                </v-btn>

                <!--DAMAGED LOST-->
                <v-btn
                  v-if="item.part_id && item.part_id.status === 0"
                  small
                  color="#a6160b"
                  variant="outlined"
                  @click="movement(item.part_id)"
                >
                  {{ $t("parts.status.recycled") }}
                </v-btn>
              </v-row>
            </td>
            <!--------------------------------------------------------------------------------------->
          </tr>
          <tr v-if="sales && sales.length < 1">
            <td :colspan="headers.length" class="text-center pa-4">
              <v-alert type="info" border="left" color="red lighten-2" text>
                No data available.
              </v-alert>
            </td>
          </tr>
        </template>
      </v-data-table-server>
      </StickyHScroll>
    </div>

    <generalAlert ref="generalAlert" />
  </div>
</template>

<script>
import moment from "moment";
import VueQrCode from "vue-qr-generator";
import generalAlert from "@/components/generalAlert.vue";
import getRequest from "../../api/getRequest";
import putRequest from "../../api/putRequest";

let lastRequestId = 0;

export default {
  components: {
    "qr-code": VueQrCode,
    generalAlert,
  },
  props: ["search"], // eslint-disable-line vue/require-prop-types
  data: () => ({
    loading: false,
    activeRow: null,
    pagination: {
      itemsPerPage: 5,
      page: 1,
      groupBy: ["date_created"],
      sortBy: [{ key: "date_created", order: "desc" }],
      sortDesc: [true],
    },
    totalItems: 0,
    sales: [],
    headers: [
      {
        title: " Part ID",
        sortable: false,
        key: " part.old_id",
      },
      {
        title: " Actions",
        sortable: false,
        key: " ",
      },
      {
        title: " ",
        sortable: true,
        key: " shipment_id",
      },
      {
        title: " Title",
        sortable: false,
        key: " part.title",
      },
      {
        title: " Manufacturer Code",
        sortable: false,
        key: " part.manufacturer_code",
      },
      {
        title: " PRA Number",
        sortable: false,
        key: " part.pra",
      },
      {
        title: " Images",
        sortable: false,
        key: " ",
      },
      {
        title: " New",
        sortable: false,
        key: " isNewPart",
      },
      {
        title: " Part Notes",
        sortable: false,
        key: " ",
      },
      {
        title: " Sale Notes",
        sortable: false,
        key: " notes",
      },
      {
        title: " Client",
        sortable: false,
        key: " client.name",
      },
      {
        title: " Debt",
        sortable: false,
        key: " debt",
      },
      {
        title: " Price",
        sortable: true,
        key: " part.price",
      },
      // {
      //   title: ' Location',
      //   sortable: false,
      //   key: ' ',
      // },
      {
        title: " Quality",
        sortable: false,
        key: " ",
      },
      {
        title: " Warranty",
        sortable: false,
        key: " warranty.title",
      },
      {
        title: " Date Created",
        sortable: true,
        key: " ",
      },
      {
        title: " Return Date",
        sortable: true,
        key: " return_date",
      },
      {
        title: " Status",
        sortable: true,
        key: " status",
      },
    ],
  }),
  computed: {
    server() {
      return this.$serverUri;
    },
    searchObject() {
      return this.$props.search;
    },
    currentDate() {
      return new Date().toISOString();
    },
    mobileSaleCards() {
      return (this.sales || []).map((item, index) => ({
        item,
        index,
        mobilePrice: this.buildSaleMobilePrice(item),
        qrId: this.getSaleQrId(item),
        imageSrc: this.getSaleImageSrc(item),
        title: this.getSaleTitle(item),
        statusLabel: this.getMobileSaleStatusLabel(item),
        statusClickable: this.getMobileSaleStatusClickable(item),
      }));
    },
  },
  watch: {
    pagination: {
      handler() {
        if (this.searchObject.type === null) {
          this.getSales();
        } else if (this.searchObject.type === "regularSearch") {
          this.regularSearch(this.searchObject.data);
        } else if (this.searchObject.type === "advancedSearch") {
          this.advancedSearch(this.searchObject.data);
        } else {
          //
        }
      },
      deep: true,
    },
    searchObject: {
      handler(value) {
        if (value.type === null) {
          this.getSales();
        } else if (value.type === "regularSearch") {
          this.regularSearch(value.data);
        } else if (value.type === "advancedSearch") {
          this.advancedSearch(value.data);
        } else {
          //
        }
      },
    },
    sales(value) {
      this.$emit("items", value);
    },
  },
  mounted() {
    if (this.$store.getters.hasUnpaidInvoices) {
      this.$nextTick(() => {
        if (this.$refs.generalAlert) {
          this.$refs.generalAlert.open({
            title: this.$t("unpaid_invoice_alert_title"),
            message: this.$t("unpaid_invoice_alert_message"),
            type: "info",
          });
        }
      });
    }
  },
  created() {
    /**
     * Add institution to table headers for LEKO Admin.
     */
    if (
      this.$store.getters.role === "LekoAdmin" ||
      this.$store.getters.role === "SuperAdmin"
    ) {
      this.headers.splice(4, 0, {
        title: " Institution",
        sortable: false,
        key: " institution_id",
      });
    }
    this.getLocale();
  },
  methods: {
    checkCar(item) {
      if (item.part_id == null || item.part_id.car_id == null) return true;
      else return false;
    },
    viewCar(carId) {
      this.$emit("view-car", { carId });
    },
    getLocale() {
      this.headers[0].title = this.$t("sales.table.part_id");
      this.headers[1].title = this.$t("sales.table.actions");
      this.headers[2].title = this.$t("sales.table.shipment");
      this.headers[3].title = this.$t("sales.table.title");
      this.headers[4].title = this.$t("sales.table.manufacturer_code");
      this.headers[5].title = this.$t("sales.table.pra");
      this.headers[6].title =
        this.$store.getters.role === "LekoAdmin" ||
        this.$store.getters.role === "SuperAdmin"
          ? this.$t("sales.table.institution")
          : this.$t("sales.table.images");
      this.headers[7].title =
        this.$store.getters.role === "LekoAdmin" ||
        this.$store.getters.role === "SuperAdmin"
          ? this.$t("sales.table.images")
          : this.$t("sales.table.new");
      this.headers[8].title =
        this.$store.getters.role === "LekoAdmin" ||
        this.$store.getters.role === "SuperAdmin"
          ? this.$t("sales.table.new")
          : this.$t("sales.table.part_notes");
      this.headers[9].title =
        this.$store.getters.role === "LekoAdmin" ||
        this.$store.getters.role === "SuperAdmin"
          ? this.$t("sales.table.part_notes")
          : this.$t("sales.table.sale_notes");
      this.headers[10].title =
        this.$store.getters.role === "LekoAdmin" ||
        this.$store.getters.role === "SuperAdmin"
          ? this.$t("sales.table.sale_notes")
          : this.$t("sales.table.client");
      this.headers[11].title =
        this.$store.getters.role === "LekoAdmin" ||
        this.$store.getters.role === "SuperAdmin"
          ? this.$t("sales.table.client")
          : this.$t("sales.table.debt");
      this.headers[12].title =
        this.$store.getters.role === "LekoAdmin" ||
        this.$store.getters.role === "SuperAdmin"
          ? this.$t("sales.table.debt")
          : this.$t("sales.table.price");
      this.headers[13].title =
        this.$store.getters.role === "LekoAdmin" ||
        this.$store.getters.role === "SuperAdmin"
          ? this.$t("sales.table.price")
          : this.$t("sales.table.quality");
      // this.headers[14].title = this.$store.getters.role === 'LekoAdmin' || this.$store.getters.role === 'SuperAdmin'
      //   ? this.$t('sales.table.location')
      //   : this.$t('sales.table.quality');
      this.headers[14].title =
        this.$store.getters.role === "LekoAdmin" ||
        this.$store.getters.role === "SuperAdmin"
          ? this.$t("sales.table.quality")
          : this.$t("sales.table.warranty");
      this.headers[15].title =
        this.$store.getters.role === "LekoAdmin" ||
        this.$store.getters.role === "SuperAdmin"
          ? this.$t("sales.table.warranty")
          : this.$t("sales.table.date_created");
      this.headers[16].title =
        this.$store.getters.role === "LekoAdmin" ||
        this.$store.getters.role === "SuperAdmin"
          ? this.$t("sales.table.date_created")
          : this.$t("sales.table.return_date");
      this.headers[17].title =
        this.$store.getters.role === "LekoAdmin" ||
        this.$store.getters.role === "SuperAdmin"
          ? this.$t("sales.table.return_date")
          : this.$t("sales.table.status");
      if (
        this.$store.getters.role === "LekoAdmin" ||
        this.$store.getters.role === "SuperAdmin"
      ) {
        this.headers[18].title = this.$t("sales.table.status");
      }
    },
    getSales() {
      const requestId = ++lastRequestId;
      this.loading = true;
      const sort = this.pagination.sortBy?.[0] || {
        key: "date_created",
        order: "desc",
      };
      getRequest(
        "/sale/datatable",
        `institution_id=${this.$store.getters.institutionId}&role=${
          this.$store.getters.role
        }&limit=${this.pagination.itemsPerPage}&page=${
          this.pagination.page
        }&sort_by=${sort.key}&descending=${sort.order === "desc"}`,
      )
        .then((response) => {
          if (requestId !== lastRequestId) return;
          this.totalItems = response.count;
          this.sales = response.sales;
          this.loading = false;
        })
        .catch((error) => {
          if (error) {
            if (requestId !== lastRequestId) return;
            this.loading = false;
          }
        });
    },
    regularSearch(data) {
      const requestId = ++lastRequestId;
      this.loading = true;
      const sort = this.pagination.sortBy?.[0] || {
        key: "date_created",
        order: "desc",
      };
      getRequest(
        "/sale/search/regular",
        `institution_id=${this.$store.getters.institutionId}&role=${
          this.$store.getters.role
        }&limit=${this.pagination.itemsPerPage}&page=${
          this.pagination.page
        }&sort_by=${sort.key}&descending=${
          sort.order === "desc"
        }&search_field=${data.searchField}&title_code=${data.titleCode}&type=${
          data.type
        }&language=${this.$store.getters.language}`,
      )
        .then((response) => {
          if (requestId !== lastRequestId) return;
          this.totalItems = response.count;
          this.sales = response.sales;
          this.loading = false;
        })
        .catch((error) => {
          if (error) {
            if (requestId !== lastRequestId) return;
            this.loading = false;
          }
        })
        .finally(() => {
          if (requestId !== lastRequestId) return;
        });
    },
    advancedSearch(data) {
      const requestId = ++lastRequestId;
      this.loading = true;
      const sort = this.pagination.sortBy?.[0] || {
        key: "date_created",
        order: "desc",
      };
      getRequest(
        "/sale/search/advanced",
        `institution_id=${this.$store.getters.institutionId}&role=${
          this.$store.getters.role
        }&limit=${this.pagination.itemsPerPage}&page=${
          this.pagination.page
        }&sort_by=${sort.key}&descending=${
          sort.order === "desc"
        }&search_field=${data.searchField}&language=${
          this.$store.getters.language
        }&title_code=${data.titleCode}&type=${data.type}&brand=${
          data.advancedSearchItems.brand
        }&model=${data.advancedSearchItems.model}&version=${
          data.advancedSearchItems.version
        }&status=${data.advancedSearchItems.status}&start_date=${
          data.advancedSearchItems.startDate
        }&end_date=${data.advancedSearchItems.endDate}&institution=${
          data.advancedSearchItems.institution
        }&client=${
          data.advancedSearchItems.client
        }&price=${data.advancedSearchItems.price}`,
      )
        .then((response) => {
          if (requestId !== lastRequestId) return;
          this.totalItems = response.count;
          this.sales = response.sales;
          this.loading = false;
        })
        .catch((error) => {
          if (error) {
            if (requestId !== lastRequestId) return;
            this.loading = false;
          }
        })
        .finally(() => {
          if (requestId !== lastRequestId) return;
        });
    },
    details(data) {
      this.$emit("details", data);
    },
    returnE(data) {
      this.$emit("return", data);
    },
    movement(data) {
      this.$emit("movement", data);
    },
    print(data) {
      this.$emit("print", data);
    },
    showImages(id, images, date) {
      this.$emit("images", {
        type: "string",
        id,
        images,
        date,
      });
    },
    showResImages(id, images) {
      this.$emit("images", {
        type: "string",
        id,
        images,
        path: "reservations",
      });
    },
    showNotes(item) {
      let notes = null;
      if (item.reservation_part && item.reservation_part.notes) {
        notes = item.reservation_part.notes;
      } else if (item.part_id && item.part_id.notes) {
        notes = item.part_id.notes;
      }
      this.$emit("show-notes", notes);
    },
    updatePagination(options) {
      this.pagination = {
        ...options,
        itemsPerPage: options.itemsPerPage ? options.itemsPerPage : 5,
        groupBy:
          options.groupBy && options.groupBy.length
            ? options.groupBy
            : ["date_created"],
        sortBy:
          options.sortBy && options.sortBy.length
            ? options.sortBy
            : { key: "date_created", order: "desc" },
        sortDesc:
          options.sortDesc && options.sortDesc.length
            ? options.sortDesc
            : [true],
      };
      this.activeRow = null;
    },
    saveNotes(id, notes) {
      putRequest("/sale/notes", `id=${id}`, { notes })
        .then(() => {
          this.refreshSales();
        })
        .catch(() => {});
    },
    formatDate(date) {
      if (!date) return "";
      moment.locale(localStorage.getItem("language") || "en");
      return moment(date).format("LLL");
    },
    refreshSales() {
      if (this.searchObject.type === null) {
        this.getSales();
      } else if (this.searchObject.type === "regularSearch") {
        this.regularSearch(this.searchObject.data);
      } else if (this.searchObject.type === "advancedSearch") {
        this.advancedSearch(this.searchObject.data);
      } else {
        //
      }
    },
    getPosition(code) {
      switch (code) {
        case "S001":
          return this.$t("left");
        case "S002":
          return this.$t("right");
        default:
          return "";
      }
    },
    difference(value) {
      const diff = value.toString();
      return diff.substring(0, 6);
    },
    openQrDialog(data) {
      this.$emit("qr", data);
    },
    share(data) {
      this.$emit("share", data);
    },
    viewPart(data) {
      this.$emit("view-part", data);
    },
    getSaleQrId(item) {
      if (item.part?.old_id) {
        return item.part.old_id.slice(
          item.part.old_id.length - 6,
          item.part.old_id.length,
        );
      }
      if (item.part?._id) {
        return item.part._id.slice(
          item.part._id.length - 6,
          item.part._id.length,
        );
      }
      return "";
    },
    openSaleQr(item) {
      if (item.part?.old_id) {
        this.openQrDialog({
          id: item.part.old_id,
          type: "inventory",
          printed: item.part.printed,
        });
      } else if (item.part?._id) {
        this.openQrDialog({
          id: item.part._id,
          type: "inventory",
          printed: item.part.printed,
        });
      }
    },
    getSaleImageSrc(item) {
      if (
        item.type === "Reservation" &&
        item.reservation_part_id?._id &&
        item.reservation_part_id?.images
      ) {
        return `https://leko-tech.fra1.cdn.digitaloceanspaces.com/${item.reservation_part_id.images[0]}`;
      }
      if (
        item.part_id?._id &&
        item.part_id?.images?.length > 0 &&
        item.part_id.images[0].thumb
      ) {
        return `https://leko-tech.fra1.cdn.digitaloceanspaces.com/${item.part_id.images[0].thumb}`;
      }
      return "https://portal.lekotech.com/logo_small.png";
    },
    onSaleImageClick(item) {
      if (
        item.type === "Reservation" &&
        item.reservation_part_id?._id &&
        item.reservation_part_id?.images
      ) {
        this.showResImages(
          item.reservation_part_id._id,
          item.reservation_part_id.images,
        );
      } else if (item.part_id?._id && item.part_id?.images) {
        this.showImages(
          item.part_id._id,
          item.part_id.images,
          item.part_id.date_created,
        );
      }
    },
    getSaleTitle(item) {
      if (item.type === "Reservation") {
        return item.reservation_part?.description || "";
      }
      if (
        item.part_id?.infoCar?.description?.descriptionId?.title
      ) {
        const title =
          item.part_id.infoCar.description.descriptionId.title[
            this.$store.getters.language
          ];
        const positionCode = item.part_id.infoCar.description.positionCode;
        const position = positionCode
          ? this.getPosition(positionCode)
          : "";
        return `${title}${position ? ` ${position}` : ""}`.trim();
      }
      return "";
    },
    buildSaleMobilePrice(item) {
      if (
        item.type === "Reservation" &&
        item.reservation_part?.price &&
        item.reservation_part?.currency
      ) {
        return {
          text: `${item.reservation_part.price} ${item.reservation_part.currency}`,
          isDiscounted: false,
          original: null,
        };
      }
      if (item.part?.price && item.part?.currency) {
        const discount = this.calculateDiscountedPrice(item.part.price, item);
        return {
          text: `${discount.finalPrice} ${item.part.currency}`,
          isDiscounted: discount.isDiscounted,
          original: `${item.part.price} ${item.part.currency}`,
        };
      }
      return null;
    },
    getMobileSaleStatusLabel(item) {
      if (item.status === "Sold" && !item.part_id) {
        return this.$t("sales.table.sold_desk");
      }
      if (item.status === "SoldCustomShop" && !item.part_id) {
        return this.$t("sales.table.sold_ebay");
      }
      if (!item.part_id) return item.status || "";

      const partStatus = item.part_id.status;

      if (partStatus === 5 && item.status === "SoldCustomShop") {
        if (item.customShopUser === "multibreves") {
          if (item.customShopPlatformSold === "Ovoko") {
            return this.$t("sales.table.sold_ovoko");
          }
          return this.$t("sales.table.sold_ebay");
        }
        if (item.customShopUser === "lekoeBay") {
          return this.$t("sales.table.sold_ebay_leko");
        }
        return this.$t("sales.table.sold_online");
      }
      if (
        partStatus === 5 &&
        item.status === "Sold" &&
        item.customShopPlatformSold === "ReteRicambi"
      ) {
        return this.$t("sales.table.sold_retericambi");
      }
      if (partStatus === 5 && item.status === "Sold") {
        return this.$t("sales.table.sold_desk");
      }
      if (partStatus === 1) return this.$t("parts.status.entered");
      if (partStatus === 2) return this.$t("parts.status.returned");
      if (partStatus === 2.1) return this.$t("parts.status.returned_shop");
      if (partStatus === 3) return this.$t("parts.status.re_entered");
      if (partStatus === 4) return this.$t("parts.status.found");
      if (partStatus === 6) return this.$t("parts.status.lost");
      if (partStatus === 6.1) return this.$t("parts.status.lost_shop");
      if (partStatus === 7) return this.$t("parts.status.damaged");
      if (partStatus === 8) return this.$t("parts.status.damaged_sales");
      if (partStatus === 8.1) return this.$t("parts.status.damaged_shop");
      if (partStatus === 9) return this.$t("parts.status.damaged_shop");
      if (partStatus === 0) return this.$t("parts.status.recycled");

      return item.status || "";
    },
    getMobileSaleStatusClickable(item) {
      return Boolean(item.part_id);
    },
    calculateDiscountedPrice(originalPrice, item) {
      // 1. Check the sales platform first
      const isReteRicambi = item.customShopPlatformSold === "ReteRicambi";

      // Ensure originalPrice is a number, defaulting to 0 if invalid
      const price = parseFloat(originalPrice) || 0;

      // RR discount percentage (NOT fraction yet)
      let rrPercent = 0;

      // 2. Apply tiered %RR ONLY if sold on ReteRicambi
      if (isReteRicambi) {
        if (price >= 0 && price <= 299) {
          rrPercent = 20;
        } else if (price >= 300 && price <= 999) {
          rrPercent = 15;
        } else if (price >= 1000 && price <= 1999) {
          rrPercent = 10;
        } else if (price >= 2000) {
          rrPercent = 5;
        }
      }

      // Convert % to fraction
      const discountRateFraction = rrPercent / 100;

      // 3. Calculate Final Price using RR formula ONLY if ReteRicambi
      let finalPrice = price;
      const isDiscounted = rrPercent > 0;

      if (isDiscounted) {
        // RR FORMULA:
        // FINAL = price - %RR + 7% + shipping
        const shipping =
          item.part_id &&
          item.part_id.shippingCosts &&
          item.part_id.shippingCosts.rr
            ? parseFloat(item.part_id.shippingCosts.rr)
            : 0;
        finalPrice =
          price - price * discountRateFraction + price * 0.07 + shipping;
      }

      // 4. Format and Return (same as your original structure)
      return {
        finalPrice: finalPrice.toFixed(2),
        discount: rrPercent, // same variable name (percentage)
        isDiscounted: isDiscounted,
      };
    },
  },
};
</script>
<style scoped>
.overline {
  text-decoration: line-through;
}

/* ── Mobile / Desktop visibility toggle ── */
@media (max-width: 959.98px) {
  .sales-table-host :deep(.v-table__wrapper),
  .sales-table-host :deep(.v-data-table__wrapper),
  .sales-table-host :deep(.v-data-table__wrapper__scroll) {
    display: none !important;
  }
  .sales-table-host :deep(.global-sticky-hscroll) {
    display: none !important;
  }
  .sales-table-host :deep(.v-data-table-footer) {
    padding-inline: 12px;
  }
}
@media (min-width: 960px) {
  .sales-mobile-list {
    display: none !important;
  }
}

/* ── Mobile card styles ── */
.sales-mobile-card {
  position: relative;
  background: #fff;
  border-radius: 8px;
  margin-bottom: 10px;
  box-shadow: 0 1px 4px rgba(0, 0, 0, 0.1);
  overflow: hidden;
}
.sales-mobile-card--active {
  background-color: #ffeb3b;
}
.sales-mobile-card--finished {
  background-color: #4caf50;
}
.sales-mobile-card__price {
  position: absolute;
  top: 6px;
  right: 6px;
  font-size: 0.7rem !important;
  max-width: 55%;
  height: auto !important;
  min-height: 0 !important;
}
.sales-mobile-card--selected {
  outline: 2px solid rgb(var(--v-theme-primary));
}
.sales-mobile-card__main {
  display: flex;
  align-items: flex-start;
  padding: 8px;
  gap: 8px;
}
.sales-mobile-card__media {
  flex-shrink: 0;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 4px;
}
.sales-mobile-card__image-btn {
  flex-shrink: 0;
  padding: 0;
  border: 0;
  background: transparent;
  cursor: pointer;
}
.sales-mobile-card__img {
  width: 72px;
  height: 72px;
  object-fit: cover;
  border-radius: 4px;
  background: #212121;
  display: block;
}
.sales-mobile-card__qr-btn {
  min-width: 72px;
  margin-top: 2px;
  padding: 4px 8px;
  border: 1px solid #bdbdbd;
  border-radius: 4px;
  background: #fff;
  font-size: 0.72rem;
  font-weight: 600;
  line-height: 1.2;
  color: #424242;
  cursor: pointer;
  text-align: center;
}
.sales-mobile-card__info {
  flex: 1;
  min-width: 0;
}
.sales-mobile-card__info-top {
  padding-right: 88px;
}
.sales-mobile-card__actions {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  border-top: 1px solid rgba(0, 0, 0, 0.08);
  padding: 2px 4px;
}
</style>
